import Api from '@/config/api';
import axios from 'axios';
import router from '@/router';

const REFRESH_ERROR_MISSING = 'missing_refresh_token';
const REFRESH_ERROR_INVALID = 'invalid_refresh_token';

const apiClient = axios.create({
  baseURL: Api.BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.handlers = [];
apiClient.interceptors.response.handlers = [];

apiClient.interceptors.request.use(
  function (config) {
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

function clearSessionAndRedirect() {
  localStorage.removeItem('oauth_session');
  router.push('/');
}

let refreshPromise = null;

function requestKey(config) {
  if (!config) return '';
  return [
    (config.method || 'get').toLowerCase(),
    config.url || '',
    JSON.stringify(config.params || {}),
    JSON.stringify(config.data !== undefined ? config.data : {}),
  ].join('|');
}

function handleRefreshThenRetry(originalRequest) {
  if (!originalRequest || !originalRequest.url) {
    return Promise.reject(originalRequest);
  }
  const isRefreshRequest =
    String(originalRequest.url).indexOf(Api.endpoints.OAUTH_REFRESH) !== -1;

  if (isRefreshRequest) {
    clearSessionAndRedirect();
    return Promise.reject(originalRequest);
  }

  if (!refreshPromise) {
    refreshPromise = apiClient
      .post(Api.endpoints.OAUTH_REFRESH)
      .then(function () {
        return apiClient(originalRequest);
      })
      .then(function (result) {
        refreshPromise = null;
        return result;
      })
      .catch(function (err) {
        refreshPromise = null;
        clearSessionAndRedirect();
        return Promise.reject(err);
      });
  }

  return refreshPromise.then(function (result) {
    if (result && result.config === originalRequest) {
      return result;
    }
    if (
      result &&
      result.config &&
      requestKey(originalRequest) === requestKey(result.config)
    ) {
      return result;
    }
    return apiClient(originalRequest);
  });
}

apiClient.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const status = error.response && error.response.status;
    const errorCode =
      error.response &&
      error.response.data &&
      error.response.data.error;
    const isRefreshError =
      errorCode === REFRESH_ERROR_MISSING ||
      errorCode === REFRESH_ERROR_INVALID;
    
    // Skip refresh logic if request has skipAuthRefresh flag
    const shouldSkipRefresh = error.config && error.config.skipAuthRefresh;

    if (status === 401 && !shouldSkipRefresh) {
      if (isRefreshError) {
        clearSessionAndRedirect();
        return Promise.reject(error);
      }
      return handleRefreshThenRetry(error.config);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
